import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { timesheetKeys, type ClockLocation, type TimesheetEntry } from '@/hooks/useTimesheet'

/**
 * EP-154: clock in and out from the dashboard. The server stamps the time;
 * each tap carries the selfie's S3 key and the phone's location, both
 * required (owner, 2026-10-06).
 */
export interface ShiftCurrentResponse {
  open: TimesheetEntry | null
  today: { date: string; entries: TimesheetEntry[]; hours: number }
}

export interface TeamTodayRow {
  user: { id: number; name: string; email: string; role: string }
  open: TimesheetEntry | null
  hours_today: number
  entries: TimesheetEntry[]
}

export interface TeamTodayResponse {
  date: string
  users: TeamTodayRow[]
}

export interface ClockProof {
  photo_key: string
  location: ClockLocation
}

export const shiftClockKeys = {
  current: () => [...timesheetKeys.all, 'clock', 'current'] as const,
  team: () => [...timesheetKeys.all, 'clock', 'team'] as const,
}

export function useShiftCurrent(enabled = true) {
  return useQuery({
    queryKey: shiftClockKeys.current(),
    queryFn: async () => {
      const { data } = await api.get<{ data: ShiftCurrentResponse }>('/api/timesheet_entries', { params: { current: 1 } })
      return data.data
    },
    enabled,
    // While a shift is open the card shows the elapsed time, so keep it fresh.
    refetchInterval: (query) => (query.state.data?.open ? 60_000 : false),
  })
}

export function useTeamToday(enabled = true) {
  return useQuery({
    queryKey: shiftClockKeys.team(),
    queryFn: async () => {
      const { data } = await api.get<{ data: TeamTodayResponse }>('/api/timesheet_entries', { params: { today: 1 } })
      return data.data
    },
    enabled,
    refetchInterval: 60_000,
  })
}

export type ClockAction = 'in' | 'out'

// One POST for both: `clock: in|out` on the resource (owner, 2026-10-07).
export function useClock() {
  const queryClient = useQueryClient()
  return useMutation({
    // The card shows the server's answer inside the button; no backstop toast.
    meta: { suppressErrorToast: true },
    mutationFn: async ({ clock, ...proof }: ClockProof & { clock: ClockAction }) => {
      const { data } = await api.post<{ data: TimesheetEntry }>('/api/timesheet_entries', { clock, ...proof })
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shiftClockKeys.current() })
      queryClient.invalidateQueries({ queryKey: shiftClockKeys.team() })
      queryClient.invalidateQueries({ queryKey: timesheetKeys.weeks() })
    },
  })
}

/** Uploads the selfie the way timesheet attachments are uploaded, returns the bare S3 key. */
export async function uploadSelfie(file: File): Promise<string> {
  const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
    content_type: file.type || 'image/jpeg',
  })
  await fetch(presigned.url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'image/jpeg' } })
  return presigned.key
}
